import { Animated, FlatList, Image, Modal, ScrollView, StyleSheet, View, Pressable, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "../../../genericPages/components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useRef, useState } from "react";
import { PlaceColors } from "../../../genericPages/helpers/PlaceColors";
import { BasePage } from "../../../genericPages/helpers/BasePage";
import { useTranslation } from "react-i18next";
import { FindifySimpleHeader, SimpleHeaderAction } from "../../../genericPages/components/FindifySimpleHeader";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { makeIconFilterButton } from "../../../genericPages/helpers/Components";
import { FindifyBasicModal } from "../../../genericPages/components/FindifyBasicModal";
import { PlaceText } from "../../../genericPages/components/PlaceText";
import { BaseFindifyList } from "../../../genericPages/components/FindifyList";
import { getTranslatedString } from "../../../genericPages/helpers/FindifyTranslatedString";
import { isVisiblePublicItem, PlaceListInitialUseEffect, PlaceListOnSelectedCategoryChangeEffect, loadContentsPageFromApi } from "@/layouts/genericPages/helpers/PlaceListHelper";
import { getContentIdForModule, getFeaturedImageUrl, makeContentDate, makeContentTitle } from "@/layouts/genericPages/helpers/DetailsHelpers";
import Icon from "react-native-vector-icons/Ionicons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BottomNavigationPadding } from "../../../genericPages/components/FindifyNavigation";
import { BiscainhosAppHeader } from "../../../genericPages/components/BiscainhosAppHeader";
import { useSeoTitle } from "../../../genericPages/helpers/SeoTitle";
import { getFileUrl } from "@/config/environment";
import { getWorkspaceTitle } from "../../../genericPages/helpers/WorkspaceText";
import { getApiFromModule } from "../../../genericPages/helpers/GetContentsByModule";
import { FindifyHTMLRender } from "../../../genericPages/helpers/FindifyHTMLRender";

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const localizedText = (findifyDataContext: any, pt: string, en: string) => (
    isEnglishLanguage(findifyDataContext?.language) ? en : pt
);

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

const getCategoryIds = (content: any): number[] => (content?.categories ?? [])
    .map((line: any) => Number(line?.category_id ?? line?.category?.category_id ?? line))
    .filter(Number.isFinite);

const makeFileUrl = (filePath?: string) => {
    if (!filePath) return undefined;
    if (/^https?:\/\//i.test(filePath)) return encodeURI(filePath);
    return encodeURI(`${getFileUrl()}${filePath}`);
}

const getImageUrl = (media: any) => (
    (typeof media === 'string' ? media : undefined)
    || media?.image_url
    || media?.featured_image_url
    || media?.media_url
    || media?.file_url_path
    || media?.file_url
    || media?.url
    || makeFileUrl(media?.file_path)
);

const getContentImageUrl = (content: any) => (
    content?.featured_image_url
    || content?.image_url
    || content?.featured_image_media_url
    || content?.image_media_url
    || getImageUrl(content?.featured_image_media)
    || getImageUrl(content?.image_media)
    || makeFileUrl(content?.featured_image)
    || makeFileUrl(content?.image)
);

const getCategoryImage = (category: any) => (
    getContentImageUrl(category)
);

const getMapImageUrl = (content: any) => (
    content?.image_plant_localization_url
    || content?.map_image_url
    || content?.floor_plan_url
    || content?.plant_url
    || getImageUrl(content?.image_plant_localization_media)
    || getImageUrl(content?.map_image_media)
    || getImageUrl(content?.map_media)
    || getImageUrl(content?.floor_plan_media)
    || getImageUrl(content?.plant_media)
    || makeFileUrl(content?.map_image)
    || makeFileUrl(content?.floor_plan)
    || makeFileUrl(content?.plant)
);

const getPoiImages = (poi: any) => {
    const images = [
        getContentImageUrl(poi),
        ...(poi?.galleries ?? [])
            .map((gallery: any) => gallery?.image_url || getImageUrl(gallery?.image_media)),
    ].filter(Boolean);

    return Array.from(new Set(images));
}

const getMediaUrl = (media: any) => (
    (typeof media === 'string' ? media : undefined)
    || media?.media_url
    || media?.file_url_path
    || media?.file_url
    || media?.url
    || makeFileUrl(media?.file_path)
);

const getAudioUrl = (item: any) => (
    item?.audio_url
    || item?.file_audio_url
    || item?.audio_guide_url
    || getMediaUrl(item?.audio_media)
    || getMediaUrl(item?.file_audio_media)
    || getMediaUrl(item?.audio_guide_media)
    || getMediaUrl(item?.audio)
    || getMediaUrl(item?.file_audio)
    || makeFileUrl(item?.audio_path)
    || item?.audio_url
);

const hasAudioGuide = (item: any) => (
    !!getAudioUrl(item)
);

const getThreeDUrl = (item: any) => (
    item?.model_3d_url
    || item?.three_d_url
    || item?.file_3d_url
    || getMediaUrl(item?.file_3d_media)
    || getMediaUrl(item?.model_3d_media)
    || getMediaUrl(item?.three_d_media)
    || getMediaUrl(item?.file_3d)
    || makeFileUrl(item?.model_3d_path)
    || item?.model_3d_url
);

const hasThreeDContent = (item: any) => (
    !!getThreeDUrl(item)
);

const getVideoUrl = (item: any) => (
    item?.video_url
    || item?.file_video_url
    || getMediaUrl(item?.file_video_media)
    || getMediaUrl(item?.video_media)
    || getMediaUrl(item?.file_video)
    || makeFileUrl(item?.video_path)
    || item?.video_url
);

const hasVideoContent = (item: any) => (
    !!getVideoUrl(item)
);

const isCategoryBaseContent = (content: any) => (
    content?.show_in_highlight_section === true
    || content?.show_in_highlight_section === 1
    || content?.show_in_highlight_section === '1'
    || content?.show_in_highlight_section === 'true'
);

const getImagePointCoordinates = (value: unknown) => {
    const match = String(value ?? '').match(/POINT\s*\(\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\s*\)/i);
    if (!match) return null;
    const x = Number(match[1]);
    const y = Number(match[2]);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

const slugifyCategoryPart = (value: string) => (
    `${value ?? ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
);

const getCategorySlug = (category: any, findifyDataContext: any) => (
    category?.slug
    || category?.page_slug
    || category?.category_slug
    || slugifyCategoryPart(getTranslatedString(category?.name, findifyDataContext))
);

const parsePoiCategory = (category: any, findifyDataContext: any) => {
    const translatedName = getTranslatedString(category?.name, findifyDataContext);
    const parts = `${translatedName ?? ''}`.split('#').filter(Boolean).map((part: string) => part.trim());
    const slug = getCategorySlug(category, findifyDataContext);
    const explicitSlug = category?.slug || category?.page_slug || category?.category_slug;

    if (parts.length >= 3) {
        return {
            category,
            level: 'nucleus',
            floorSlug: parts[0],
            roomSlug: parts[1],
            title: parts.slice(2).join(' - '),
            slug,
        };
    }

    if (parts.length === 2) {
        return {
            category,
            level: 'room',
            floorSlug: parts[0],
            roomSlug: explicitSlug || slugifyCategoryPart(parts[1]),
            title: parts[1],
            slug: explicitSlug || slugifyCategoryPart(parts[1]),
        };
    }

    if (/^floor-\d+/i.test(slug)) {
        return {
            category,
            level: 'floor',
            floorSlug: slug,
            title: translatedName,
            slug,
        };
    }

    return {
        category,
        level: 'room',
        floorSlug: '',
        roomSlug: slug,
        title: translatedName,
        slug,
    };
}

const buildPoiCategoryHierarchy = (categories: any[], findifyDataContext: any) => {
    const parsed = (categories ?? []).map((category: any) => parsePoiCategory(category, findifyDataContext));
    const floors = parsed.filter((item: any) => item.level === 'floor');
    const rooms = parsed.filter((item: any) => item.level === 'room');
    const nuclei = parsed.filter((item: any) => item.level === 'nucleus');
    const hasStructuredRooms = rooms.some((item: any) => !!item.floorSlug);

    return {
        parsed,
        floors: floors.length > 0 ? floors : (hasStructuredRooms ? [] : [{
            category: rooms[0]?.category,
            level: 'floor',
            floorSlug: '',
            title: '',
            slug: '',
        }]),
        rooms,
        nuclei,
    };
}

// Newer API responses carry the category relation explicitly.  Use it whenever
// it is available instead of inferring floors and rooms from a display name.
const getParentCategoryId = (category: any) => {
    const parentId = category?.parent_category_id
        ?? category?.parent?.category_id
        ?? category?.parent_category?.category_id;
    return parentId == null || parentId === '' ? undefined : Number(parentId);
};

const buildCategoryTree = (categories: any[], findifyDataContext: any) => {
    const items = (categories ?? []).map((category: any) => ({
        category,
        title: getTranslatedString(category?.name, findifyDataContext),
        parentId: getParentCategoryId(category),
    }));
    const byId = new Map(items.map((item: any) => [Number(item.category?.category_id), item]));
    const hasParentTree = items.some((item: any) => item.parentId != null && byId.has(item.parentId));
    const childrenByParentId = new Map<number, any[]>();
    if (hasParentTree) {
        items.forEach((item: any) => {
            if (item.parentId == null || !byId.has(item.parentId)) return;
            const children = childrenByParentId.get(item.parentId) ?? [];
            children.push(item);
            childrenByParentId.set(item.parentId, children);
        });
    }
    const roots = hasParentTree
        ? items.filter((item: any) => item.parentId == null || !byId.has(item.parentId))
        : [];
    const getDescendants = (categoryId?: number) => {
        if (!categoryId || !hasParentTree) return [];
        const descendants: any[] = [];
        const pending = [...(childrenByParentId.get(categoryId) ?? [])];
        while (pending.length) {
            const item = pending.shift();
            descendants.push(item);
            pending.push(...(childrenByParentId.get(Number(item.category?.category_id)) ?? []));
        }
        return descendants;
    };
    return { hasParentTree, items, byId, roots, childrenByParentId, getDescendants };
}

export const PlaceContentListTab = ({ navigation, ...props }: any) => {
    const [contents, setContents] = useState([]);
    const { t } = useTranslation();
    const [module, setModule] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const findifyDataContext = useContext(FindifyDataContext);
    const [loaded, setLoaded] = useState(false);
    const [categoryFiltersModalOpen, setCategoriesModalOpen] = useState(false);
    const [availableModuleCategories, setAvailableModuleCategories] = useState<any[]>([]);
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const [selectedPoiIndex, setSelectedPoiIndex] = useState(0);
    const [selectedPoiImageIndex, setSelectedPoiImageIndex] = useState(0);
    const [mapSelectedContent, setMapSelectedContent] = useState<any>();
    const [poiModalOpen, setPoiModalOpen] = useState(false);
    const [roomModalOpen, setRoomModalOpen] = useState(false);
    const [roomMapLayout, setRoomMapLayout] = useState({ width: 0, height: 0 });
    const [roomMapImageSize, setRoomMapImageSize] = useState({ width: 0, height: 0 });
    const [roomMapContents, setRoomMapContents] = useState<any[]>([]);
    const [mapFocusedContentId, setMapFocusedContentId] = useState<number | undefined>();
    const [activeCategoryPlanImageUrl, setActiveCategoryPlanImageUrl] = useState<string | undefined>();
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
    const [threeDModalUrl, setThreeDModalUrl] = useState<string | null>(null);
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const [activeAudioUrl, setActiveAudioUrl] = useState<string | null>(null);
    const audioRef = useRef<any>(null);
    const imageScale = useRef(new Animated.Value(1)).current;
    const insets = useSafeAreaInsets();
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;

    useEffect(() => {
        PlaceListInitialUseEffect({
            props,
            setContents,
            setLoaded,
            setModule,
            setSelectedCategories,
            findifyDataContext,
            setAvailableModuleCategories,
            contents,
        });
    }, [])


    useEffect(() => {
        PlaceListOnSelectedCategoryChangeEffect({
            loaded,
            selectedCategories,
            setContents,
            module,
            findifyDataContext,
            setActionsList,
            availableModuleCategories,
            t,
        });
        setSelectedPoiIndex(0);
        setSelectedPoiImageIndex(0);
        setInfoModalOpen(false);
    }, [selectedCategories])

    const isBiscainhosPoiList = Number(findifyDataContext?.workspace?.workspace_id) === 156 && module === 'points_of_interest';
    const hasMultipleContentCategories = new Set(contents.flatMap(getCategoryIds)).size > 1;
    const shouldShowCategoryFilter = isBiscainhosPoiList
        ? hasMultipleContentCategories
        : availableModuleCategories.length > 1;
    const poiCategoryHierarchy = buildPoiCategoryHierarchy(availableModuleCategories, findifyDataContext);
    const categoryTree = buildCategoryTree(availableModuleCategories, findifyDataContext);
    const hasActiveCategory = selectedCategories.length === 1;
    const activeTreeItem = hasActiveCategory ? categoryTree.byId.get(Number(selectedCategories[0])) : undefined;
    const activeParsedCategory = selectedCategories.length === 1
        ? poiCategoryHierarchy.parsed.find((cat: any) => cat.category?.category_id === selectedCategories[0])
        : undefined;
    const activeFloorSlug = activeParsedCategory?.floorSlug ?? '';
    const activeRoomSlug = activeParsedCategory?.roomSlug
        ?? (activeFloorSlug ? poiCategoryHierarchy.rooms.find((room: any) => room.floorSlug === activeFloorSlug)?.roomSlug : '')
        ?? '';
    const legacyVisibleFloors = poiCategoryHierarchy.floors.filter((floor: any) => !!floor.category);
    const legacyVisibleRooms = poiCategoryHierarchy.rooms.filter((room: any) => (
        activeFloorSlug ? room.floorSlug === activeFloorSlug : !room.floorSlug
    ));
    const legacyVisibleNuclei = poiCategoryHierarchy.nuclei.filter((nucleus: any) => nucleus.floorSlug === activeFloorSlug && nucleus.roomSlug === activeRoomSlug);
    const visibleFloors = categoryTree.hasParentTree ? categoryTree.roots : legacyVisibleFloors;
    const visibleRooms = categoryTree.hasParentTree && activeTreeItem
        ? (categoryTree.childrenByParentId.get(Number(activeTreeItem.category.category_id)) ?? [])
        : legacyVisibleRooms;
    const visibleNuclei = categoryTree.hasParentTree && activeTreeItem
        ? (categoryTree.childrenByParentId.get(Number(activeTreeItem.category.category_id)) ?? [])
        : legacyVisibleNuclei;
    const activeCategory = selectedCategories.length === 1
        ? availableModuleCategories.find((cat: any) => cat.category_id === selectedCategories[0])
        : undefined;
    const activeCategoryTitle = activeTreeItem?.title || activeParsedCategory?.title || getTranslatedString(activeCategory?.name, findifyDataContext) || localizedText(findifyDataContext, 'Pisos', 'Floors');

    const header = <FindifySimpleHeader navigation={navigation}
        // module if no category filter or multiple category filter, single cat filter shows cat title
        title={
            selectedCategories.length === 1 ?
                activeCategoryTitle :
                t('modules.content_types.' + module)
        }
        actions={actionsList}
        image={findifyDataContext?.workspace.app_settings?.app_banner_url} />;

    const openContent = (content: any) => {
        const contentId = getContentIdForModule(content, module);
        navigation.push('PlaceContentDetails', {
            module: module,
            content_id: contentId,
            page_slug: content.page_slug
        });
    }

    const listTitle = selectedCategories.length === 1
        ? activeCategoryTitle
        : localizedText(findifyDataContext, 'Pontos de Interesse', 'Points of Interest');
    useSeoTitle(listTitle || t('modules.content_types.' + module), getWorkspaceTitle(findifyDataContext));
    const displayContents = hasActiveCategory ? contents : [];
    const heroImage = getContentImageUrl(displayContents?.[0]) || findifyDataContext?.workspace?.app_settings?.app_banner_url;
    const colors = PlaceColors(findifyDataContext);
    const activeCategoryLevel = categoryTree.hasParentTree
        ? (activeTreeItem?.parentId == null ? 'floor' : 'room')
        : (activeParsedCategory?.level ?? 'room');
    const parentTreeItem = categoryTree.hasParentTree && activeTreeItem?.parentId != null
        ? categoryTree.byId.get(activeTreeItem.parentId)
        : undefined;
    const parentFloor = activeCategoryLevel === 'room' || activeCategoryLevel === 'nucleus'
        ? visibleFloors.find((floor: any) => floor.floorSlug === activeFloorSlug)
        : undefined;
    const parentRoom = activeCategoryLevel === 'nucleus'
        ? visibleRooms.find((room: any) => room.roomSlug === activeRoomSlug)
        : undefined;
    const parentNavigation = categoryTree.hasParentTree && parentTreeItem?.category
        ? {
            title: `${localizedText(findifyDataContext, 'Voltar a', 'Back to')} ${parentTreeItem.title}`,
            icon: 'arrow-up-outline',
            onPress: () => setSelectedCategories([parentTreeItem.category.category_id]),
        }
        : activeCategoryLevel === 'floor'
        ? {
            title: localizedText(findifyDataContext, 'Voltar aos pisos', 'Back to floors'),
            icon: 'arrow-up-outline',
            onPress: () => setSelectedCategories([]),
        }
        : activeCategoryLevel === 'room' && parentFloor?.category
            ? {
                title: localizedText(findifyDataContext, 'Voltar ao piso', 'Back to floor'),
                icon: 'arrow-up-outline',
                onPress: () => setSelectedCategories([parentFloor.category.category_id]),
            }
            : activeCategoryLevel === 'nucleus' && parentRoom?.category
                ? {
                    title: localizedText(findifyDataContext, 'Voltar à sala', 'Back to room'),
                    icon: 'arrow-up-outline',
                    onPress: () => setSelectedCategories([parentRoom.category.category_id]),
                }
                : undefined;
    const childNavigationSections = [
        ...(!hasActiveCategory && visibleFloors.length > 0 ? [{
            title: localizedText(findifyDataContext, 'Pisos', 'Floors'),
            icon: 'layers-outline',
            items: visibleFloors,
        }] : []),
        ...(hasActiveCategory && categoryTree.hasParentTree && visibleRooms.length > 0 ? [{
            title: localizedText(findifyDataContext, 'Explorar', 'Explore'),
            icon: 'business-outline',
            items: visibleRooms,
        }] : []),
        ...(hasActiveCategory && !categoryTree.hasParentTree && activeCategoryLevel === 'floor' && visibleRooms.length > 0 ? [{
            title: localizedText(findifyDataContext, 'Salas', 'Rooms'),
            icon: 'business-outline',
            items: visibleRooms,
        }] : []),
        ...(hasActiveCategory && !categoryTree.hasParentTree && activeCategoryLevel === 'room' && visibleNuclei.length > 0 ? [{
            title: localizedText(findifyDataContext, 'Núcleos', 'Nuclei'),
            icon: 'albums-outline',
            items: visibleNuclei,
        }] : []),
    ];
    const getCategoryNavigationImage = (category: any) => {
        const categoryId = Number(category?.category_id);
        const highlightedContents = contents.filter(isCategoryBaseContent);
        // A category's own highlighted content is its cover. Only fall back to
        // one of its descendants when it has no directly associated cover.
        const directBaseContent = highlightedContents.find((content: any) => (
            isCategoryBaseContent(content)
            && getCategoryIds(content).includes(categoryId)
        ));
        const descendantCategoryIds = new Set(
            categoryTree.getDescendants(categoryId).map((item: any) => Number(item.category?.category_id)),
        );
        const descendantBaseContent = highlightedContents.find((content: any) => (
            getCategoryIds(content).some((contentCategoryId) => descendantCategoryIds.has(contentCategoryId))
        ));
        return getContentImageUrl(directBaseContent) || getContentImageUrl(descendantBaseContent) || getCategoryImage(category);
    };
    const roomDescendantCategoryIds = categoryTree.hasParentTree
        ? categoryTree.getDescendants(activeCategory?.category_id).map((item: any) => item.category.category_id)
        : activeCategoryLevel === 'room'
        ? poiCategoryHierarchy.parsed
            .filter((item: any) => (
                item.category?.category_id !== activeCategory?.category_id
                && item.floorSlug === activeFloorSlug
                && item.roomSlug === activeRoomSlug
            ))
            .map((item: any) => item.category.category_id)
        : [];
    // The category image is the room plan. Keep it separate from the image
    // presented in the room header, which belongs to the highlighted base content.
    const activeCategoryPlanImage = hasActiveCategory
        ? activeCategoryPlanImageUrl || getCategoryImage(activeCategory)
        : undefined;
    const requestedBaseContentId = props?.route?.params?.base_content_id;
    const baseContent: any = displayContents.find((item: any) => (
        requestedBaseContentId != null && String(getContentIdForModule(item, module)) === String(requestedBaseContentId)
    )) || displayContents.find((item: any) => isCategoryBaseContent(item));
    const roomHeroTitle = baseContent
        ? makeContentTitle(baseContent, module, findifyDataContext)
        : activeCategoryTitle;
    const activeCategoryImage = hasActiveCategory
        ? getContentImageUrl(baseContent) || activeCategoryPlanImage
        : undefined;
    useEffect(() => {
        if (!activeCategory?.category_id) {
            setActiveCategoryPlanImageUrl(undefined);
            return;
        }
        const categoriesApi = getApiFromModule('categories', findifyDataContext);
        // The public detail endpoint can respond with an empty body for a
        // category. The public category list includes its expanded media.
        categoriesApi?.getList(0, 300, null, { category_type: 'points_of_interest' }, null)
            .then((data: any) => {
                const category = data?.results?.find((item: any) => item.category_id === activeCategory.category_id);
                setActiveCategoryPlanImageUrl(getCategoryImage(category));
            })
            .catch(() => setActiveCategoryPlanImageUrl(undefined));
    }, [activeCategory?.category_id, findifyDataContext?.workspace?.workspace_id]);
    useEffect(() => {
        if (module !== 'points_of_interest' || !activeCategory?.category_id) {
            setRoomMapContents([]);
            return;
        }

        // A room plan includes markers from the room itself and every lower
        // category level identified under that room.
        const categoryIds = Array.from(new Set([
            activeCategory.category_id,
            ...roomDescendantCategoryIds,
        ]));
        const api = getApiFromModule(module, findifyDataContext);
        api?.getList(0, 300, null, {
            'categories.category.category_id_in': JSON.stringify(categoryIds),
        }, null).then((data: any) => setRoomMapContents((data?.results ?? []).filter(isVisiblePublicItem)))
            .catch(() => setRoomMapContents([]));
    }, [activeCategory?.category_id, activeRoomSlug, roomDescendantCategoryIds.join(','), module, findifyDataContext?.workspace?.workspace_id]);
    const pieceContents = displayContents.filter((item: any) => item !== baseContent);
    const requestedContentId = props?.route?.params?.selected_content_id;
    const pieceContentIdsKey = pieceContents.map((item: any) => getContentIdForModule(item, module)).join(',');
    useEffect(() => {
        if (requestedContentId == null) return;
        if (baseContent && String(getContentIdForModule(baseContent, module)) === String(requestedContentId)) {
            setSelectedPoiImageIndex(0);
            setInfoModalOpen(true);
            setPoiModalOpen(true);
            return;
        }
        const selectedIndex = pieceContents.findIndex((item: any) => String(getContentIdForModule(item, module)) === String(requestedContentId));
        if (selectedIndex < 0) return;
        setSelectedPoiIndex(selectedIndex);
        setSelectedPoiImageIndex(0);
        setInfoModalOpen(false);
        setPoiModalOpen(true);
    }, [requestedContentId, pieceContentIdsKey, baseContent?.point_of_interest_id, module]);
    const selectedPoi: any = pieceContents[selectedPoiIndex];
    const isMapContentOpen = !!mapSelectedContent;
    const modalContent = mapSelectedContent ?? (infoModalOpen ? baseContent : selectedPoi);
    const modalContentImages = getPoiImages(modalContent);
    const modalContentImage = modalContentImages[selectedPoiImageIndex] ?? modalContentImages[0];
    const modalContentTitle = modalContent ? makeContentTitle(modalContent, module, findifyDataContext) : '';
    const modalContentDescriptionHtml = getTranslatedString(modalContent?.description || modalContent?.resume, findifyDataContext) || '';
    const modalContentAudioUrl = getAudioUrl(modalContent);
    const modalContentHasAudio = hasAudioGuide(modalContent);
    const modalContentThreeDUrl = getThreeDUrl(modalContent);
    const modalContentHasThreeD = hasThreeDContent(modalContent);
    const modalContentVideoUrl = getVideoUrl(modalContent);
    const modalContentHasVideo = hasVideoContent(modalContent);
    const modalContentCoordinates = getImagePointCoordinates(modalContent?.x_y_cord);
    const canGoPreviousPoi = !isMapContentOpen && selectedPoiIndex > 0;
    const canGoNextPoi = !isMapContentOpen && selectedPoiIndex < pieceContents.length - 1;
    const roomMapScale = roomMapLayout.width > 0 && roomMapLayout.height > 0 && roomMapImageSize.width > 0 && roomMapImageSize.height > 0
        ? Math.min(roomMapLayout.width / roomMapImageSize.width, roomMapLayout.height / roomMapImageSize.height)
        : 0;
    const roomMapPoints = roomMapContents
        .map((content: any) => ({ content, coordinates: getImagePointCoordinates(content?.x_y_cord) }))
        .filter((point: any) => point.coordinates);
    const defaultMapFocusedContentId = getContentIdForModule(baseContent, module);
    const effectiveMapFocusedContentId = mapFocusedContentId ?? defaultMapFocusedContentId;
    const focusedMapContent = roomMapContents.find((content: any) => (
        getContentIdForModule(content, module) === effectiveMapFocusedContentId
    )) ?? mapSelectedContent;
    // Before a marker is selected, the highlighted base content is the source
    // of the room plan. After selecting one, the selected piece may override it.
    const mapImageSource = focusedMapContent ?? baseContent;
    const mapImageLocalizationId = mapImageSource?.image_plant_localization;
    const mapImageCategory = availableModuleCategories.find((category: any) => (
        mapImageLocalizationId != null && String(category?.featured_image) === String(mapImageLocalizationId)
    ));
    const localizedMapImage = mapImageLocalizationId && String(activeCategory?.featured_image) === String(mapImageLocalizationId)
        ? activeCategoryPlanImage
        : getImageUrl(mapImageCategory?.featured_image_media);
    const roomMapImage = getMapImageUrl(focusedMapContent)
        || localizedMapImage
        || getMapImageUrl(baseContent)
        || getContentImageUrl(baseContent)
        || activeCategoryPlanImage;

    useEffect(() => {
        if (!roomMapImage) return;
        Image.getSize(roomMapImage, (width, height) => setRoomMapImageSize({ width, height }), () => setRoomMapImageSize({ width: 0, height: 0 }));
    }, [roomMapImage]);
    useEffect(() => {
        if (props?.route?.params?.open_map && roomMapImage) setRoomModalOpen(true);
    }, [props?.route?.params?.open_map, roomMapImage]);

    const openPoiModal = (index: number) => {
        setSelectedPoiIndex(index);
        setSelectedPoiImageIndex(0);
        setInfoModalOpen(false);
        imageScale.setValue(1);
        setPoiModalOpen(true);
    }

    const openInfoModal = () => {
        if (!baseContent) {
            setRoomModalOpen(true);
            return;
        }
        setSelectedPoiImageIndex(0);
        setInfoModalOpen(true);
        imageScale.setValue(1);
        setPoiModalOpen(true);
    }

    const openRoomMap = () => {
        // Open immediately; the category detail request supplies the plan image
        // moments later when it is not present in the category list response.
        setRoomModalOpen(true);
    }

    const openModalContentMap = () => {
        const contentId = getContentIdForModule(modalContent, module);
        if (!modalContentCoordinates || !contentId) return;
        setMapFocusedContentId(contentId);
        setPoiModalOpen(false);
        setRoomModalOpen(true);
    }

    const openRoomMapPoint = (content: any) => {
        setRoomModalOpen(false);
        setMapFocusedContentId(undefined);
        setMapSelectedContent(content);
        setSelectedPoiImageIndex(0);
        setInfoModalOpen(false);
        imageScale.setValue(1);
        setPoiModalOpen(true);
    }

    const closePoiModal = () => {
        audioRef.current?.pause?.();
        audioRef.current = null;
        setAudioPlaying(false);
        setAudioCurrentTime(0);
        setAudioDuration(0);
        setActiveAudioUrl(null);
        setInfoModalOpen(false);
        setMapSelectedContent(undefined);
        imageScale.setValue(1);
        setPoiModalOpen(false);
    }

    const goToPoiIndex = (index: number) => {
        if (infoModalOpen || index < 0 || index >= pieceContents.length) return;
        audioRef.current?.pause?.();
        setAudioPlaying(false);
        setAudioCurrentTime(0);
        setAudioDuration(0);
        setActiveAudioUrl(null);
        imageScale.setValue(1);
        setSelectedPoiIndex(index);
        setSelectedPoiImageIndex(0);
    }

    const goToPoiImageIndex = (index: number) => {
        if (index < 0 || index >= modalContentImages.length) return;
        setSelectedPoiImageIndex(index);
    }

    const formatAudioTime = (seconds?: number) => {
        if (!seconds || !Number.isFinite(seconds)) return '0:00';
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    const toggleAudio = (audioUrl?: string) => {
        if (!audioUrl || typeof window === 'undefined') return;
        if (audioRef.current && activeAudioUrl === audioUrl) {
            if (audioRef.current.paused) {
                audioRef.current.play?.().catch((error: any) => console.error('Failed to play audio:', error));
            } else {
                audioRef.current.pause?.();
            }
            return;
        }

        audioRef.current?.pause?.();
        audioRef.current = new Audio(audioUrl);
        setActiveAudioUrl(audioUrl);
        setAudioCurrentTime(0);
        setAudioDuration(0);
        audioRef.current.addEventListener('loadedmetadata', () => {
            setAudioDuration(audioRef.current?.duration ?? 0);
        });
        audioRef.current.addEventListener('timeupdate', () => {
            setAudioCurrentTime(audioRef.current?.currentTime ?? 0);
            setAudioDuration(audioRef.current?.duration ?? 0);
        });
        audioRef.current.addEventListener('play', () => setAudioPlaying(true));
        audioRef.current.addEventListener('pause', () => setAudioPlaying(false));
        audioRef.current.addEventListener('ended', () => {
            setAudioPlaying(false);
            setAudioCurrentTime(0);
        });
        audioRef.current.play?.().catch((error: any) => console.error('Failed to play audio:', error));
    }

    const openThreeD = (threeDUrl?: string) => {
        if (!threeDUrl) return;
        if (typeof document !== 'undefined' && !document.querySelector('script[data-model-viewer]')) {
            const script = document.createElement('script');
            script.type = 'module';
            script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
            script.setAttribute('data-model-viewer', 'true');
            document.head.appendChild(script);
        }
        setThreeDModalUrl(threeDUrl);
    }

    const openVideo = (videoUrl?: string) => {
        if (!videoUrl) return;
        setVideoModalUrl(videoUrl);
    }

    const handleDetailsScroll = (event: any) => {
        const y = event?.nativeEvent?.contentOffset?.y ?? 0;
        Animated.spring(imageScale, {
            toValue: y > 24 ? 0.94 : 1,
            useNativeDriver: true,
            speed: 18,
            bounciness: 4,
        }).start();
    }

    if (isBiscainhosPoiList) {
        return <BasePage navigation={navigation}>
            <View style={[styles.biscainhosListPage, { backgroundColor: colors.background.get() }]}>
                <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} allowBackFallback={false} />
                <FlatList
                    key="mdds-room-poi-list"
                    data={pieceContents}
                    numColumns={1}
                    keyExtractor={(item: any, index: number) => `${getContentIdForModule(item, module)}-${index}`}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={{
                        width: '100%',
                        maxWidth: isLandscapeTablet ? 1120 : isTablet ? 760 : undefined,
                        alignSelf: 'center',
                        paddingHorizontal: isTablet ? 32 : 0,
                        paddingTop: 20,
                        paddingBottom: insets.bottom + BottomNavigationPadding + 24,
                    }}
                    ListHeaderComponent={() => (
                        <View>
                            <Pressable
                                style={[styles.guidedVisitStrip, { backgroundColor: colors.primary.get() }]}
                                onPress={() => navigation.push('PlantsPage', {
                                    category_id: activeCategory?.category_id,
                                })}>
                                <View style={styles.guidedVisitIcon}>
                                    <Icon name="navigate-outline" size={24} color="#fff" />
                                </View>
                                <View style={styles.guidedVisitCopy}>
                                    <PlaceText style={styles.guidedVisitTitle}>
                                        {localizedText(findifyDataContext, 'Visita guiada', 'Guided visit')}
                                    </PlaceText>
                                    <PlaceText style={styles.guidedVisitText}>
                                        {localizedText(findifyDataContext, 'Percurso recomendado por pisos', 'Recommended route by floor')}
                                    </PlaceText>
                                </View>
                                <Icon name="chevron-forward-circle" size={30} color="rgba(255,255,255,0.82)" />
                            </Pressable>

                            {hasActiveCategory ? <View style={styles.roomHero}>
                                {activeCategoryImage ? <Image source={{ uri: activeCategoryImage }} resizeMode="cover" style={styles.roomHeroImage} /> : null}
                                <View style={styles.roomHeroTopShade} />
                                <View style={styles.roomHeroGradient} />
                                <View style={styles.roomHeroText}>
                                    <PlaceText style={styles.roomHeroTitle}>{roomHeroTitle}</PlaceText>
                                    {pieceContents.length > 0 ? <View style={styles.roomHeroMetaRow}>
                                        <PlaceText style={styles.roomHeroCount}>
                                            {pieceContents.length} {localizedText(findifyDataContext, pieceContents.length === 1 ? 'peça' : 'peças', pieceContents.length === 1 ? 'piece' : 'pieces')}
                                        </PlaceText>
                                    </View> : null}
                                </View>
                                <Pressable style={styles.roomHeroActions} onPress={(event: any) => {
                                    event?.stopPropagation?.();
                                    openRoomMap();
                                }}>
                                    <Icon name="map-outline" size={34} color="rgba(255,255,255,0.76)" />
                                </Pressable>
                            </View> : null}

                            <View style={[styles.roomObjectsHeader, { paddingHorizontal: isTablet ? 0 : 20 }]}>
                                {hasActiveCategory ? <Pressable style={styles.roomInfoRow} onPress={openInfoModal}>
                                    <View style={styles.roomInfoIcon}>
                                        <Icon name="information-circle-outline" size={28} color="#fff" />
                                    </View>
                                    <PlaceText style={[styles.roomInfoText, { color: colors.text.get() }]}>
                                        {roomHeroTitle}
                                    </PlaceText>
                                    <Icon name="chevron-forward-circle" size={30} color={colors.text.withAlpha(150)} />
                                </Pressable> : null}
                                {parentNavigation ? <Pressable style={styles.parentNavigationRow} onPress={parentNavigation.onPress}>
                                    <View style={[styles.parentNavigationIcon, { borderColor: colors.primary.withAlpha(90) }]}>
                                        <Icon name={parentNavigation.icon} size={22} color={colors.primary.get()} />
                                    </View>
                                    <PlaceText style={[styles.parentNavigationText, { color: colors.primary.get() }]}>
                                        {parentNavigation.title}
                                    </PlaceText>
                                </Pressable> : null}
                                {childNavigationSections.map((section: any) => (
                                    <View key={`child_navigation_section_${section.title}`} style={styles.childNavigationSection}>
                                        <PlaceText style={[styles.roomObjectsTitle, { color: colors.text.get() }]}>
                                            {section.title}
                                        </PlaceText>
                                        {section.items.map((item: any) => {
                                            const image = getCategoryNavigationImage(item.category);
                                            const selected = item.category.category_id === activeCategory?.category_id;
                                            return <Pressable
                                                key={`child_navigation_${item.category.category_id}`}
                                                style={[styles.roomPoiRow, selected ? styles.childNavigationSelectedRow : null]}
                                                disabled={selected}
                                                onPress={() => setSelectedCategories([item.category.category_id])}>
                                                <View style={styles.roomPoiThumbWrap}>
                                                    {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.roomPoiThumb} /> : (
                                                        <View style={[styles.childNavigationFallbackThumb, { backgroundColor: colors.primary.withAlpha(45) }]}>
                                                            <Icon
                                                                name={section.icon}
                                                                size={30}
                                                                color={colors.primary.get()}
                                                            />
                                                        </View>
                                                    )}
                                                </View>
                                                <View style={styles.roomPoiCopy}>
                                                    <PlaceText numberOfLines={2} style={[styles.roomPoiTitle, { color: selected ? colors.primary.get() : colors.text.withAlpha(210) }]}>
                                                        {item.title}
                                                    </PlaceText>
                                                </View>
                                                {selected ? <Icon name="checkmark-circle" size={30} color={colors.primary.get()} /> : <Icon name="chevron-forward-circle" size={32} color={colors.text.withAlpha(150)} />}
                                            </Pressable>
                                        })}
                                    </View>
                                ))}
                                {pieceContents.length > 0 ? <PlaceText style={[styles.roomObjectsTitle, { color: colors.text.get() }]}>
                                    {localizedText(findifyDataContext, 'Aqui podemos ver', 'Here you can see')}
                                </PlaceText> : null}
                            </View>
                        </View>
                    )}
                    renderItem={({ item, index }: any) => {
                        const image = getPoiImages(item)[0];
                        const title = makeContentTitle(item, module, findifyDataContext);
                        const hasAudio = hasAudioGuide(item);
                        return <Pressable
                            style={[
                                styles.roomPoiRow,
                                { paddingHorizontal: isTablet ? 0 : 20 }
                            ]}
                            onPress={() => openPoiModal(index)}>
                            <View style={styles.roomPoiThumbWrap}>
                                {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.roomPoiThumb} /> : null}
                            </View>
                            <View style={styles.roomPoiCopy}>
                                <PlaceText numberOfLines={2} style={[styles.roomPoiTitle, { color: colors.text.withAlpha(210) }]}>{title}</PlaceText>
                                {hasAudio ? <View style={styles.roomPoiAudioRow}>
                                    <Icon name="headset-outline" size={22} color={colors.text.withAlpha(170)} />
                                </View> : null}
                            </View>
                            <Icon name="chevron-forward-circle" size={32} color={colors.text.withAlpha(150)} />
                        </Pressable>
                    }}
                />
                <Modal visible={roomModalOpen} transparent={false} animationType="slide" onRequestClose={() => setRoomModalOpen(false)}>
                    <View style={styles.poiSlideModal}>
                        <Pressable style={styles.poiSlideClose} onPress={() => setRoomModalOpen(false)}>
                            <Icon name="close-outline" size={42} color="#fff" />
                        </Pressable>
                        <ScrollView
                            style={{ flex: 1 }}
                            contentContainerStyle={styles.roomMapContent}>
                            <View style={styles.poiSlideImageStage} onLayout={({ nativeEvent }) => setRoomMapLayout(nativeEvent.layout)}>
                                {roomMapImage ? <Image source={{ uri: roomMapImage }} resizeMode="contain" style={styles.poiSlideMainImage} /> : null}
                                {roomMapScale > 0 && roomMapPoints.map((mapPoint: any) => {
                                    const markerSize = 38;
                                    return <Pressable
                                        key={`room-map-${getContentIdForModule(mapPoint.content, module)}`}
                                        accessibilityLabel={makeContentTitle(mapPoint.content, module, findifyDataContext)}
                                        onPressIn={(event: any) => {
                                            event?.stopPropagation?.();
                                            openRoomMapPoint(mapPoint.content);
                                        }}
                                        style={[styles.roomMapPointMarker, {
                                            left: (roomMapLayout.width - roomMapImageSize.width * roomMapScale) / 2 + mapPoint.coordinates.x * roomMapScale - markerSize / 2,
                                            top: (roomMapLayout.height - roomMapImageSize.height * roomMapScale) / 2 + mapPoint.coordinates.y * roomMapScale - markerSize * 0.9,
                                        }]}>
                                        <Icon
                                            name="location"
                                            size={markerSize}
                                            color={effectiveMapFocusedContentId === getContentIdForModule(mapPoint.content, module) ? colors.primary.get() : "#747474"}
                                        />
                                    </Pressable>
                                })}
                            </View>
                        </ScrollView>
                    </View>
                </Modal>
                <Modal visible={poiModalOpen} transparent={false} animationType="slide" onRequestClose={closePoiModal}>
                    <View style={styles.poiSlideModal}>
                        <Pressable style={styles.poiSlideClose} onPress={closePoiModal}>
                            <Icon name="close-outline" size={42} color="#fff" />
                        </Pressable>
                        <PlaceText style={styles.poiSlideCounter}>
                            {!infoModalOpen && !isMapContentOpen && pieceContents.length > 0 ? `${selectedPoiIndex + 1} / ${pieceContents.length}` : ''}
                        </PlaceText>
                        <ScrollView
                            key={`poi_details_${modalContent?.point_of_interest_id ?? selectedPoiIndex}_${infoModalOpen ? 'info' : 'piece'}`}
                            style={{ flex: 1 }}
                            contentContainerStyle={styles.poiSlideContent}
                            onScroll={handleDetailsScroll}
                            scrollEventThrottle={16}>
                            <Animated.View style={[styles.poiSlideImageStage, { transform: [{ scale: imageScale }] }]}>
                                {modalContentImage ? <Image source={{ uri: modalContentImage }} resizeMode="contain" style={styles.poiSlideMainImage} /> : null}
                                {modalContentImages.length > 1 ? <>
                                    <Pressable
                                        disabled={selectedPoiImageIndex === 0}
                                        style={[styles.poiImageNavButton, styles.poiImageNavButtonLeft, selectedPoiImageIndex === 0 ? styles.poiImageNavButtonDisabled : null]}
                                        onPress={() => goToPoiImageIndex(selectedPoiImageIndex - 1)}>
                                        <Icon name="chevron-back" size={28} color={selectedPoiImageIndex === 0 ? "rgba(255,255,255,0.24)" : "#fff"} />
                                    </Pressable>
                                    <Pressable
                                        disabled={selectedPoiImageIndex === modalContentImages.length - 1}
                                        style={[styles.poiImageNavButton, styles.poiImageNavButtonRight, selectedPoiImageIndex === modalContentImages.length - 1 ? styles.poiImageNavButtonDisabled : null]}
                                        onPress={() => goToPoiImageIndex(selectedPoiImageIndex + 1)}>
                                        <Icon name="chevron-forward" size={28} color={selectedPoiImageIndex === modalContentImages.length - 1 ? "rgba(255,255,255,0.24)" : "#fff"} />
                                    </Pressable>
                                    <View style={styles.poiImageDots}>
                                        {modalContentImages.map((imageUrl: any, imageIndex: number) => (
                                            <Pressable
                                                key={`poi_image_dot_${imageIndex}_${imageUrl}`}
                                                style={[
                                                    styles.poiImageDot,
                                                    imageIndex === selectedPoiImageIndex ? styles.poiImageDotActive : null,
                                                ]}
                                                onPress={() => goToPoiImageIndex(imageIndex)}
                                            />
                                        ))}
                                    </View>
                                </> : null}
                            </Animated.View>
                            <View style={styles.poiSlideActions}>
                                <Pressable
                                    disabled={!canGoPreviousPoi || infoModalOpen || isMapContentOpen}
                                    style={[styles.poiSlideSideButton, (!canGoPreviousPoi || infoModalOpen || isMapContentOpen) ? styles.poiSlideSideButtonDisabled : null]}
                                    onPress={() => goToPoiIndex(selectedPoiIndex - 1)}>
                                    <Icon name="play-skip-back" size={30} color={canGoPreviousPoi && !infoModalOpen && !isMapContentOpen ? "rgba(255,255,255,0.76)" : "rgba(255,255,255,0.24)"} />
                                </Pressable>
                                {modalContentHasAudio ? <Pressable style={styles.poiSlideAudioButton} onPress={() => toggleAudio(modalContentAudioUrl)}>
                                    <Icon name={audioPlaying && activeAudioUrl === modalContentAudioUrl ? "pause" : "headset-outline"} size={42} color="#000" />
                                </Pressable> : <View style={styles.poiSlideAudioButtonPlaceholder} />}
                                <Pressable
                                    disabled={!canGoNextPoi || infoModalOpen || isMapContentOpen}
                                    style={[styles.poiSlideSideButton, (!canGoNextPoi || infoModalOpen || isMapContentOpen) ? styles.poiSlideSideButtonDisabled : null]}
                                    onPress={() => goToPoiIndex(selectedPoiIndex + 1)}>
                                    <Icon name="play-skip-forward" size={30} color={canGoNextPoi && !infoModalOpen && !isMapContentOpen ? "rgba(255,255,255,0.76)" : "rgba(255,255,255,0.24)"} />
                                </Pressable>
                            </View>
                            <View style={styles.poiSlideTextBlock}>
                                <PlaceText style={styles.poiSlideTitle}>{infoModalOpen ? roomHeroTitle : modalContentTitle}</PlaceText>
                                <View style={styles.poiSlideMetaRow}>
                                    {!infoModalOpen ? <PlaceText style={styles.poiSlideRoomTag}>{activeCategoryTitle}</PlaceText> : null}
                                    {modalContentHasAudio ? <Pressable style={styles.poiSlideInlineAudio} onPress={() => toggleAudio(modalContentAudioUrl)}>
                                        <Icon name={audioPlaying && activeAudioUrl === modalContentAudioUrl ? "pause" : "headset-outline"} size={24} color="#fff" />
                                        <PlaceText style={styles.poiSlideAudioTime}>
                                            {formatAudioTime(audioCurrentTime)} / -{formatAudioTime(Math.max((audioDuration || 0) - audioCurrentTime, 0))}
                                        </PlaceText>
                                    </Pressable> : null}
                                    {modalContentHasVideo ? <Pressable
                                        style={styles.poiSlideMediaButton}
                                        onPress={() => openVideo(modalContentVideoUrl)}>
                                        <Icon name="play-circle-outline" size={24} color="#fff" />
                                        <PlaceText style={styles.poiSlideMediaText}>Vídeo</PlaceText>
                                    </Pressable> : null}
                                    {modalContentHasThreeD ? <Pressable
                                        style={styles.poiSlideMediaButton}
                                        onPress={() => openThreeD(modalContentThreeDUrl)}>
                                        <Icon name="cube-outline" size={24} color="#fff" />
                                        <PlaceText style={styles.poiSlideMediaText}>3D</PlaceText>
                                    </Pressable> : null}
                                    {modalContentCoordinates ? <Pressable style={styles.poiSlideMapButton} onPress={openModalContentMap}>
                                        <Icon name="map-outline" size={34} color="rgba(255,255,255,0.78)" />
                                    </Pressable> : null}
                                </View>
                                {!!modalContentDescriptionHtml && <View style={styles.poiSlideDescription}>
                                    <FindifyHTMLRender
                                        baseStyles={{ color: 'rgba(255,255,255,0.78)', fontSize: 17, lineHeight: 25 }}
                                        tagsStyles={{ p: { marginBottom: 14 } }}
                                        classesStyles={{ PlaygroundEditorTheme__paragraph: { marginBottom: 14 } }}
                                        htmlCode={modalContentDescriptionHtml}
                                    />
                                </View>}
                                {modalContent && !infoModalOpen ? <Pressable
                                    style={styles.poiSlideDetailsButton}
                                    onPress={() => {
                                        closePoiModal();
                                        openContent(modalContent);
                                    }}>
                                    <PlaceText style={styles.poiSlideDetailsText}>{localizedText(findifyDataContext, 'Ver ficha completa', 'Open full details')}</PlaceText>
                                    <Icon name="open-outline" size={18} color="#fff" />
                                </Pressable> : null}
                            </View>
                        </ScrollView>
                    </View>
                </Modal>
                <Modal visible={!!videoModalUrl} transparent={false} animationType="fade" onRequestClose={() => setVideoModalUrl(null)}>
                    <View style={styles.videoModal}>
                        <Pressable style={styles.poiSlideClose} onPress={() => setVideoModalUrl(null)}>
                            <Icon name="close-outline" size={42} color="#fff" />
                        </Pressable>
                        {videoModalUrl ? React.createElement('video', {
                            src: videoModalUrl,
                            controls: true,
                            autoPlay: true,
                            playsInline: true,
                            style: {
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#000',
                                objectFit: 'contain',
                            },
                        }) : null}
                    </View>
                </Modal>
                <Modal visible={!!threeDModalUrl} transparent={false} animationType="fade" onRequestClose={() => setThreeDModalUrl(null)}>
                    <View style={styles.threeDModal}>
                        <Pressable style={styles.poiSlideClose} onPress={() => setThreeDModalUrl(null)}>
                            <Icon name="close-outline" size={42} color="#fff" />
                        </Pressable>
                        {threeDModalUrl ? React.createElement('model-viewer', {
                            src: threeDModalUrl,
                            ar: true,
                            'camera-controls': true,
                            'auto-rotate': true,
                            'shadow-intensity': '0.8',
                            exposure: '1',
                            style: {
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#000',
                            },
                        }) : null}
                    </View>
                </Modal>
                {shouldShowCategoryFilter ? <FindifyBasicModal
                    isOpen={categoryFiltersModalOpen}
                    closeModal={() => {
                        setCategoriesModalOpen(false)
                    }}
                    style={{
                        width: '92%',
                        maxWidth: 440,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: colors.primary.withAlpha(45),
                        backgroundColor: colors.background.get(),
                        paddingHorizontal: 18,
                        paddingTop: 10,
                        paddingBottom: 18,
                        alignItems: 'stretch',
                    }}
                >
                    <View style={styles.biscainhosFilterModal}>
                        <View style={[styles.biscainhosFilterHandle, { backgroundColor: colors.primary.withAlpha(55) }]} />
                        <View style={styles.biscainhosFilterHeader}>
                            <View>
                                <PlaceText style={[styles.biscainhosFilterTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Filtrar pontos', 'Filter places')}</PlaceText>
                                <PlaceText style={[styles.biscainhosFilterSubtitle, { color: colors.text.withAlpha(190) }]}>{localizedText(findifyDataContext, 'Escolha uma ou mais categorias', 'Choose one or more categories')}</PlaceText>
                            </View>
                            <Pressable style={[styles.biscainhosFilterClose, { backgroundColor: colors.primary.withAlpha(15) }]} onPress={() => setCategoriesModalOpen(false)}>
                                <Icon name="close-outline" size={26} color={colors.primary.get()} />
                            </Pressable>
                        </View>
                        {availableModuleCategories.map((cat: any, index: number) => {
                            const selected = selectedCategories.includes(cat.category_id);
                            return <Pressable
                                key={'avaliableLanguages' + index}
                                style={[
                                    styles.biscainhosFilterOption,
                                    {
                                        backgroundColor: colors.background.solidAlphaWhiteBase(35),
                                        borderColor: colors.primary.withAlpha(45),
                                    },
                                    selected ? {
                                        backgroundColor: colors.secondary.withAlpha(55),
                                        borderColor: colors.primary.get(),
                                    } : null
                                ]}
                                onPress={() => {
                                    if (selected) {
                                        setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                                    } else {
                                        setSelectedCategories([...selectedCategories, cat.category_id]);
                                    }
                                }}>
                                <PlaceText style={[
                                    styles.biscainhosFilterOptionText,
                                    { color: selected ? colors.primary.get() : colors.text.get() },
                                ]}>{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                                <Icon name={selected ? "checkmark-circle" : "ellipse-outline"} size={22} color={selected ? colors.primary.get() : colors.primary.withAlpha(70)} />
                            </Pressable>
                        })}
                        <View style={styles.biscainhosFilterActions}>
                            <Pressable
                                style={[styles.biscainhosFilterClearButton, { borderColor: colors.primary.get() }]}
                                onPress={() => setSelectedCategories([])}>
                                <PlaceText style={[styles.biscainhosFilterClearText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Limpar', 'Clear')}</PlaceText>
                            </Pressable>
                            <Pressable
                                style={[styles.biscainhosFilterApplyButton, { backgroundColor: colors.primary.get() }]}
                                onPress={() => setCategoriesModalOpen(false)}>
                                <PlaceText style={[styles.biscainhosFilterApplyText, { color: colors.background.get() }]}>{localizedText(findifyDataContext, 'Aplicar', 'Apply')}</PlaceText>
                            </Pressable>
                        </View>
                    </View>
                </FindifyBasicModal> : null}
            </View>
        </BasePage>
    }

    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        <BaseFindifyList
            items={contents.map((content: any) => {
                const contentId = getContentIdForModule(content, module);
                return {
                    id: contentId,
                    image: getFeaturedImageUrl(content),
                    title: makeContentTitle(content, module, findifyDataContext),
                    resume: getTranslatedString(content.resume, findifyDataContext),
                    date: makeContentDate(content, module),
                    onPress: () => {
                        navigation.push('PlaceContentDetails', {
                            module: module,
                            content_id: contentId,
                            page_slug: content.page_slug
                        })
                    }
                }
            })}
            header={header}
            onEndReached={() => {
                if (loaded) {
                    loadContentsPageFromApi(selectedCategories, contents, module, findifyDataContext, setContents);
                }
            }}
            enableGrid={true}
            selectLabel={t('list.read_more')}
            actions={shouldShowCategoryFilter ? <>
                {makeIconFilterButton(() => setCategoriesModalOpen(true), "funnel-outline", selectedCategories.length !== 0, findifyDataContext)}
            </> : null}
        />

        {shouldShowCategoryFilter ? <FindifyBasicModal
            isOpen={categoryFiltersModalOpen}
            closeModal={() => {
                setCategoriesModalOpen(false)
            }}
        >
            <View style={styles.optionsZoneOptionsModal}>
                {availableModuleCategories.map((cat: any, index: number) => {
                    return <Pressable
                        key={'avaliableLanguages' + index}
                        onPress={() => {
                            // findifyDataContext?.setLanguage(lang.tag);
                            if (selectedCategories.includes(cat.category_id)) {
                                setSelectedCategories(selectedCategories.filter((c: number) => c !== cat.category_id));
                            } else {
                                setSelectedCategories([...selectedCategories, cat.category_id]);
                            }

                        }}>
                        <View style={{
                            ...styles.optionsZoneOptionModalLine,
                            ...(index !== 0 ? styles.optionTopBorder : {}),
                            ...(selectedCategories.includes(cat.category_id) ? { opacity: 0.6 } : {})
                        }}>
                            <PlaceText style={{ color: PlaceColors(findifyDataContext).text.get() }} >{getTranslatedString(cat.name, findifyDataContext)}</PlaceText>
                        </View>
                    </Pressable>
                })}
            </View>
        </FindifyBasicModal> : null}
    </BasePage >;
};


const styles = StyleSheet.create({

    container: {
        height: '100%',
        width: '100%',
    },
    map: {
        flex: 1
    },
    optionTopBorder: {
        borderTopColor: "#00000060",
        borderTopWidth: 1
    },
    optionsZoneOptionsModal: {

    },
    optionsZoneOptionModalLine: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 15,
        padding: 10,
        // width: '90%'

    },
    optionsZoneDivider: {
        width: '80%',
        marginLeft: '10%',
        marginRight: '10%',
        marginTop: 10,
        height: 2,
        borderRadius: 6,
        backgroundColor: '#00000030'
    },
    biscainhosListPage: {
        flex: 1,
    },
    roomTabs: {
        gap: 10,
        paddingBottom: 18,
    },
    floorTabs: {
        paddingBottom: 10,
    },
    nucleusTabs: {
        paddingTop: 0,
        paddingBottom: 18,
    },
    roomTab: {
        height: 46,
        minWidth: 116,
        maxWidth: 220,
        borderRadius: 0,
        borderWidth: 1,
        paddingHorizontal: 16,
        alignItems: 'center',
        justifyContent: 'center',
    },
    floorTab: {
        minWidth: 96,
    },
    nucleusTab: {
        height: 40,
        minWidth: 108,
    },
    roomTabText: {
        fontSize: 15,
        lineHeight: 20,
        fontWeight: '700',
    },
    nucleusTabText: {
        fontSize: 14,
        lineHeight: 18,
        fontWeight: '800',
    },
    roomHero: {
        height: 390,
        overflow: 'hidden',
        backgroundColor: '#050505',
        marginBottom: 28,
    },
    roomHeroImage: {
        width: '100%',
        height: '100%',
    },
    roomHeroTopShade: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 90,
        backgroundColor: 'rgba(0,0,0,0.18)',
    },
    roomHeroGradient: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 190,
        backgroundColor: 'rgba(0,0,0,0.62)',
    },
    roomHeroText: {
        position: 'absolute',
        left: 28,
        right: 24,
        bottom: 28,
    },
    roomHeroTitle: {
        color: '#fff',
        fontSize: 42,
        lineHeight: 48,
        fontWeight: '600',
    },
    roomHeroMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 18,
    },
    roomHeroCount: {
        color: 'rgba(255,255,255,0.72)',
        fontSize: 23,
        lineHeight: 29,
        fontWeight: '700',
    },
    roomHeroActions: {
        position: 'absolute',
        right: 24,
        bottom: 30,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 22,
    },
    roomMapPointMarker: {
        position: 'absolute',
        zIndex: 10,
        elevation: 10,
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomObjectsHeader: {
        marginBottom: 14,
    },
    guidedVisitStrip: {
        minHeight: 82,
        marginHorizontal: 20,
        marginBottom: 22,
        borderRadius: 6,
        paddingHorizontal: 18,
        paddingVertical: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        shadowColor: '#000',
        shadowOpacity: 0.16,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
    },
    guidedVisitIcon: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: 'rgba(255,255,255,0.16)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    guidedVisitCopy: {
        flex: 1,
        minWidth: 0,
    },
    guidedVisitTitle: {
        color: '#fff',
        fontSize: 21,
        lineHeight: 27,
        fontWeight: '800',
    },
    guidedVisitText: {
        color: 'rgba(255,255,255,0.78)',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: '600',
        marginTop: 2,
    },
    roomInfoRow: {
        minHeight: 72,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        paddingVertical: 14,
        marginBottom: 8,
    },
    roomInfoIcon: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#000',
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomInfoText: {
        flex: 1,
        fontSize: 23,
        lineHeight: 29,
        fontWeight: '600',
    },
    parentNavigationRow: {
        minHeight: 46,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 18,
    },
    parentNavigationIcon: {
        width: 34,
        height: 34,
        borderRadius: 17,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    parentNavigationText: {
        fontSize: 16,
        lineHeight: 21,
        fontWeight: '800',
    },
    roomObjectsTitle: {
        fontSize: 30,
        lineHeight: 36,
        fontWeight: '700',
    },
    childNavigationSection: {
        marginBottom: 18,
    },
    childNavigationSelectedRow: {
        opacity: 0.72,
    },
    childNavigationFallbackThumb: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomPoiRow: {
        minHeight: 116,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 18,
        paddingVertical: 12,
    },
    roomPoiThumbWrap: {
        width: 96,
        height: 96,
        backgroundColor: '#111',
    },
    roomPoiThumb: {
        width: '100%',
        height: '100%',
    },
    roomPoiCopy: {
        flex: 1,
        minWidth: 0,
    },
    roomPoiTitle: {
        fontSize: 24,
        lineHeight: 30,
        fontWeight: '500',
    },
    roomPoiAudioRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
    },
    poiSlideModal: {
        flex: 1,
        backgroundColor: '#000',
    },
    videoModal: {
        flex: 1,
        backgroundColor: '#000',
        paddingTop: 96,
        paddingBottom: 28,
        paddingHorizontal: 18,
    },
    threeDModal: {
        flex: 1,
        backgroundColor: '#000',
        paddingTop: 96,
        paddingBottom: 28,
        paddingHorizontal: 18,
    },
    poiSlideClose: {
        position: 'absolute',
        left: 22,
        top: 42,
        zIndex: 5,
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
    },
    poiSlideCounter: {
        position: 'absolute',
        right: 34,
        top: 56,
        zIndex: 5,
        color: '#fff',
        fontSize: 28,
        lineHeight: 34,
        fontWeight: '700',
    },
    poiSlideContent: {
        minHeight: '100%' as any,
        paddingTop: 96,
        paddingBottom: 40,
    },
    roomMapContent: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingTop: 70,
        paddingBottom: 30,
    },
    poiSlideImageStage: {
        position: 'relative',
        height: 470,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 28,
    },
    poiSlideMainImage: {
        width: '100%',
        height: '100%',
    },
    poiImageNavButton: {
        position: 'absolute',
        top: '45%' as any,
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: 'rgba(0,0,0,0.42)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    poiImageNavButtonLeft: {
        left: 12,
    },
    poiImageNavButtonRight: {
        right: 12,
    },
    poiImageNavButtonDisabled: {
        backgroundColor: 'rgba(0,0,0,0.16)',
    },
    poiImageDots: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 6,
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 8,
    },
    poiImageDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.35)',
    },
    poiImageDotActive: {
        width: 22,
        backgroundColor: '#fff',
    },
    poiSlideActions: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 48,
        marginTop: 8,
        marginBottom: 42,
    },
    poiSlideSideButton: {
        width: 74,
        height: 74,
        borderRadius: 37,
        backgroundColor: 'rgba(255,255,255,0.08)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    poiSlideSideButtonDisabled: {
        backgroundColor: 'rgba(255,255,255,0.03)',
    },
    poiSlideAudioButton: {
        width: 108,
        height: 108,
        borderRadius: 54,
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
    },
    poiSlideAudioButtonPlaceholder: {
        width: 108,
        height: 108,
    },
    poiSlideTextBlock: {
        paddingHorizontal: 28,
    },
    poiSlideTitle: {
        color: '#fff',
        fontSize: 36,
        lineHeight: 42,
        fontWeight: '500',
        marginBottom: 18,
    },
    poiSlideMetaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
    },
    poiSlideRoomTag: {
        color: '#fff',
        backgroundColor: '#0068a8',
        fontSize: 22,
        lineHeight: 28,
        paddingHorizontal: 12,
        paddingVertical: 6,
    },
    poiSlideRoomCount: {
        color: '#fff',
        fontSize: 22,
        lineHeight: 28,
        fontWeight: '700',
    },
    poiSlideMediaButton: {
        minHeight: 34,
        borderRadius: 17,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.46)',
        paddingHorizontal: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    poiSlideMediaText: {
        color: '#fff',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: '800',
    },
    poiSlideInlineAudio: {
        minHeight: 34,
        borderRadius: 17,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.46)',
        paddingHorizontal: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
    },
    poiSlideAudioTime: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 17,
        fontWeight: '800',
    },
    poiSlideMapButton: {
        marginLeft: 'auto',
    },
    poiSlideDescription: {
        color: 'rgba(255,255,255,0.78)',
        fontSize: 17,
        lineHeight: 25,
        marginBottom: 22,
    },
    poiSlideDetailsButton: {
        alignSelf: 'flex-start',
        minHeight: 44,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.42)',
        paddingHorizontal: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    poiSlideDetailsText: {
        color: '#fff',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: '700',
    },
    biscainhosListHeader: {
        height: 62,
        paddingHorizontal: 24,
        borderBottomWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    biscainhosHeaderIconButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosHeaderLogo: {
        width: 154,
        height: 38,
    },
    biscainhosHeaderActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    biscainhosAccessibilityButton: {
        width: 42,
        height: 42,
        borderRadius: 11,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosHeroCard: {
        height: 400,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 48,
        backgroundColor: '#111',
    },
    biscainhosHeroImage: {
        width: '100%',
        height: '100%',
    },
    biscainhosHeroOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.18)',
    },
    biscainhosHeroGradient: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: '55%' as any,
        backgroundColor: 'rgba(0,0,0,0.46)',
    },
    biscainhosHeroText: {
        position: 'absolute',
        left: 28,
        right: 24,
        bottom: 30,
    },
    biscainhosHeroEyebrow: {
        fontSize: 11,
        lineHeight: 16,
        letterSpacing: 1.6,
        fontWeight: '700',
        marginBottom: 8,
    },
    biscainhosHeroTitle: {
        color: '#fff',
        fontFamily: 'Georgia',
        fontSize: 46,
        lineHeight: 52,
        fontWeight: '700',
    },
    biscainhosPoiCard: {
        borderWidth: 1,
        marginBottom: 24,
        overflow: 'hidden',
    },
    biscainhosPoiGridRow: {
        gap: 24,
        alignItems: 'stretch',
    },
    biscainhosPoiCardGrid: {
        flex: 1,
        minWidth: 0,
    },
    biscainhosPoiCardFeatured: {
        marginBottom: 28,
    },
    biscainhosPoiImage: {
        width: '100%',
        height: 260,
    },
    biscainhosPoiImageFeatured: {
        height: 300,
    },
    biscainhosPoiBody: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: 20,
        minHeight: 150,
    },
    biscainhosPoiTitle: {
        fontFamily: 'Georgia',
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '700',
        marginBottom: 12,
    },
    biscainhosPoiResume: {
        fontSize: 14,
        lineHeight: 20,
    },
    biscainhosReadMoreRow: {
        alignItems: 'flex-end',
        marginTop: 20,
    },
    biscainhosReadMore: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: '700',
        letterSpacing: 1.1,
        borderBottomWidth: 1,
        paddingBottom: 2,
    },
    biscainhosFilterButton: {
        position: 'absolute',
        right: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.22,
        shadowRadius: 8,
        elevation: 8,
    },
    biscainhosFilterModal: {
        width: '100%',
    },
    biscainhosFilterHandle: {
        width: 46,
        height: 4,
        borderRadius: 4,
        alignSelf: 'center',
        marginBottom: 16,
    },
    biscainhosFilterHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 16,
    },
    biscainhosFilterTitle: {
        fontFamily: 'Georgia',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    biscainhosFilterSubtitle: {
        fontSize: 12,
        lineHeight: 18,
        marginTop: 4,
    },
    biscainhosFilterClose: {
        width: 38,
        height: 38,
        borderRadius: 19,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterOption: {
        minHeight: 48,
        borderRadius: 7,
        borderWidth: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 10,
    },
    biscainhosFilterOptionSelected: {
    },
    biscainhosFilterOptionText: {
        fontSize: 14,
        lineHeight: 19,
        fontWeight: '600',
        flex: 1,
        paddingRight: 10,
    },
    biscainhosFilterOptionTextSelected: {},
    biscainhosFilterActions: {
        flexDirection: 'row',
        gap: 10,
        marginTop: 8,
    },
    biscainhosFilterClearButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterClearText: {
        fontSize: 14,
        fontWeight: '700',
    },
    biscainhosFilterApplyButton: {
        flex: 1,
        minHeight: 48,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFilterApplyText: {
        fontSize: 14,
        fontWeight: '700',
    }
})
